import { inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject } from 'rxjs';
import {
    SnackbarWithDetailsButtonComponent,
    SnackbarWithDetailsButtonData,
} from './snackbar-with-details-button/snackbar-with-details-button.component';
import { SnackbarNotificationMessageQueueItem } from './SnackbarNotificationMessageQueueItem';
import { SnackbarNotificationMessageType } from './SnackbarNotificationMessageType';

interface SnackbarNotificationActionTriggeredEventData {
    // TODO: Rename queueItemId
    queueItemId: string;
}

@Injectable({
    providedIn: 'root',
})
export class SnackbarNotificationService {
    private matSnackBar = inject(MatSnackBar);
    public notificationActionTriggered = new Subject<SnackbarNotificationActionTriggeredEventData>();

    private notificationMessageQueue: SnackbarNotificationMessageQueueItem[] = [];
    private maxNotificationMessagesInQueue = 10;
    private notificationMessageQueueIsBusy = false;
    private errorNotificationDisplayTime = 10000;

    public addSuccessMessageToQueue(message: string): void {
        const queueItem = SnackbarNotificationMessageQueueItem.create({ message: message, messageType: SnackbarNotificationMessageType.Success });
        this.addNotificationMessageToQueue(queueItem);
    }

    public addInfoMessageToQueue(message: string): void {
        const queueItem = SnackbarNotificationMessageQueueItem.create({ message: message, messageType: SnackbarNotificationMessageType.Info });
        this.addNotificationMessageToQueue(queueItem);
    }

    public addWarningMesageToQueue(message: string): void {
        const queueItem = SnackbarNotificationMessageQueueItem.create({ message: message, messageType: SnackbarNotificationMessageType.Warning });
        this.addNotificationMessageToQueue(queueItem);
    }

    public addDebugMessageToQueue(message: string): void {
        const queueItem = SnackbarNotificationMessageQueueItem.create({ message: message, messageType: SnackbarNotificationMessageType.Debug });
        this.addNotificationMessageToQueue(queueItem);
    }

    public addErrorMesageToQueue(message: string): void {
        const queueItem = SnackbarNotificationMessageQueueItem.create({
            message: message,
            messageType: SnackbarNotificationMessageType.Error,
            displayTime: this.errorNotificationDisplayTime,
        });
        this.addNotificationMessageToQueue(queueItem);
    }

    public addErrorMessageWithButtonToQueue(message: string): string {
        const queueItem = SnackbarNotificationMessageQueueItem.createWithButton({
            message: message,
            messageType: SnackbarNotificationMessageType.Error,
            displayTime: this.errorNotificationDisplayTime,
        });

        this.addNotificationMessageToQueue(queueItem);

        return queueItem.id;
    }

    private addNotificationMessageToQueue(snackbarData: SnackbarNotificationMessageQueueItem): void {
        if (this.notificationMessageQueue.length > this.maxNotificationMessagesInQueue) {
            console.error('Queue item limit exceeded');
            return;
        }

        this.notificationMessageQueue.push(snackbarData);

        if (!this.notificationMessageQueueIsBusy) {
            this.showNotificationMessageFromQueue();
        }
    }

    private showNotificationMessageFromQueue(): void {
        const currentQueueItem = this.notificationMessageQueue.shift();
        if (currentQueueItem === undefined) {
            this.notificationMessageQueueIsBusy = false;
            return;
        }

        this.notificationMessageQueueIsBusy = true;

        if (currentQueueItem.hasButton) {
            this.showSnackbarWithDetailsButton(currentQueueItem);
        } else {
            this.showSimpleSnackbar(currentQueueItem);
        }
    }

    private showSimpleSnackbar(queueItem: SnackbarNotificationMessageQueueItem): void {
        const snackBarRef = this.matSnackBar.open(queueItem.message, 'x', {
            panelClass: queueItem.messageType.toString().toLowerCase() + '-snackbar',
            duration: queueItem.displayTime,
        });

        snackBarRef.afterDismissed().subscribe(() => {
            this.showNotificationMessageFromQueue();
        });
    }

    private showSnackbarWithDetailsButton(queueItem: SnackbarNotificationMessageQueueItem): void {
        const snackBarRef = this.matSnackBar.openFromComponent(SnackbarWithDetailsButtonComponent, {
            duration: queueItem.displayTime,
            panelClass: queueItem.messageType.toString().toLowerCase() + '-snackbar',
            data: new SnackbarWithDetailsButtonData({
                message: queueItem.message,
                queueItemId: queueItem.id,
            }),
        });

        snackBarRef.afterDismissed().subscribe(() => {
            this.showNotificationMessageFromQueue();
        });
    }
}
